import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';

import { UserService } from 'src/app/angularJS/services/user/user.service';

import { TapTranslationRequestService } from 'src/app/angularJS/services/tap-translation-request/tap-translation-request.service';

import { TapService } from 'src/app/angularJS/services/tap/tap.service';

import { UsageEventService } from 'src/app/angularJS/services/usage-event/usage-event.service';

import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';

/**
 * A controller that handles events related to uploading/adding tap events into the system
 */
export class TapEventsController {
  public static $inject = [
    'ACTToolbarService',
    'userService',
    '$state',
    '$q',
    'tapTranslationRequestService',
    'tapService',
    '$rootScope',
    'usageEventService',
    'breadcrumbService',
    'PrimaryAction'
  ];
  public uploadDetail;
  public tapTranslationRequests: Array<InterACT.Interfaces.TfnTaps.TfnTapTranslationRequest> = [];
  public paginatorData: InterACT.Interfaces.IPaginationData = {
    currentPage: 1,
    perPageCount: 15,
    totalCount: 0
  };
  public tapEventDictionary: InterACT.Interfaces.IDictionary<InterACT.Interfaces.TfnTaps.TfnTap> = {};
  public usageEventDictionary: InterACT.Interfaces.IDictionary<InterACT.Interfaces.UsageEvents.UsageEvent> = {};
  public usageEventCreatedDateDictionary: InterACT.Interfaces.IDictionary<string> = {};
  public showMoreFields = false;

  constructor(
    private ActToolbarService: ACTToolbarService,
    private userService: UserService,
    private $state: ng.ui.IStateService,
    private $q: ng.IQService,
    private tapTranslationRequestService: TapTranslationRequestService,
    private tapService: TapService,
    private $rootScope,
    private usageEventService: UsageEventService,
    private breadcrumbService: BreadcrumbService,
    private PrimaryAction: any
  ) {
    this.uploadDetail = {};
    this.activate();
  }

  /**
   * A method that gets invoked when a file has been imported into the client (not stored in the system, just available in the client)
   */
  public onFileImported = () => {
    const r = new FileReader();
    r.onload = (e: any) => {
      const contents = e.target.result;
      const tapEvents = this.createTapEventsFromCsvFile(contents);

      this.$state.go('tap-events-list', { tapEvents: tapEvents });
    };

    r.readAsText(this.uploadDetail.file);
  };

  /**
   * A method that gets a page worth of tap translation requests for the supplied page
   * @param page the number of the page to get the requests for
   * @param perPage the amount of requests to get
   * @returns a promise that resolves once the operation is complete
   */
  public getTapTranslationRequests = (page: number, perPage: number) => {
    const deferred = this.$q.defer();

    if (!perPage) {
      perPage = this.paginatorData.perPageCount;
    }

    this.$rootScope.showSpinner = true;
    this.tapTranslationRequestService.getList(page, perPage, 'sortBy created desc').then((response) => {

      const allPromises = [];
      for (let c = 0; c < response.tfnTapTranslationRequests.length; c++) {
        const req = response.tfnTapTranslationRequests[c];
        const tapId = req.tfnTapTranslationRequest.tfnTap.link.split('/')[2];
        allPromises.push(this.getTapEvent(tapId));

        if (req.tfnTapTranslationRequest.usageEvent) {
          const usageEventId = req.tfnTapTranslationRequest.usageEvent.link.split('/')[2];
          allPromises.push(this.getUsageEvent(usageEventId));
        }
      }

      this.$q.when(allPromises).then(() => {
        this.tapTranslationRequests = response.tfnTapTranslationRequests.map((request) => { return request.tfnTapTranslationRequest; });
        this.paginatorData.totalCount = response.meta.totalCount;
        this.$rootScope.showSpinner = false;
        deferred.resolve();
      }).catch((error) => {
        this.$rootScope.showSpinner = false;
      });
    });

    return deferred.promise;
  };

  /**
   * A method that retrieves a tap event with a given id
   * @param tapEventId the id of the tap event to retrieve
   * @returns a promise that resolves with the tap even with the supplied id
   */
  private getTapEvent(tapEventId: string) {
    const deferred = this.$q.defer();

    if (this.tapEventDictionary[tapEventId]) {
      deferred.resolve();
    } else {
      this.tapService.get(tapEventId).then((response) => {
        this.tapEventDictionary[tapEventId] = response.tfnTap;
        deferred.resolve();
      }).catch((error) => {
        deferred.reject(error);
      });
    }

    return deferred.promise;
  }

  /**
   * A method that retrieves a usage event with a given id
   * @param usageEventId the id of the tap event to retrieve
   * @returns a promise that resolves with the usage evetn with the supplied id
   */
  private getUsageEvent(usageEventId: string) {
    const deferred = this.$q.defer();

    if (this.usageEventDictionary[usageEventId]) {
      deferred.resolve();
    } else {
      this.usageEventService.get(usageEventId).then((response) => {
        this.usageEventDictionary[usageEventId] = response.usageEvent;
        this.usageEventCreatedDateDictionary[usageEventId] = (<any>response.meta).created;
        deferred.resolve();
      }).catch((error) => {
        deferred.reject(error);
      });
    }

    return deferred.promise;
  }

  /**
   * Creates multiple TfnTaps from from a csv string
   * @param csvFileContents the contents of a csv file that contains tap event data
   */
  private createTapEventsFromCsvFile(csvFileContents: string) {
    const tapEvents: Array<InterACT.Interfaces.TfnTaps.TfnTap> = [];
    const tapEventStrings = csvFileContents.split('\r\n');

    if (tapEventStrings.length <= 1) {
      return [];
    }

    const tapEventPropertyNames = tapEventStrings[0].split(',');

    for (let c = 1; c < tapEventStrings.length; c++) {
      const tapEventString = tapEventStrings[c];

      if (tapEventString.length === 0) {
        continue;
      }

      const tapEvent = this.createTapEventFromString(tapEventString, tapEventPropertyNames);
      tapEvents.push(tapEvent);
    }

    return tapEvents;
  }

  /**
   * Creates a single TfnTap from from a csv tap event string
   * @param tapEventString the csv string that contains the tap event data
   * @param tapEventHeaders the headers that belong to the csv tap event string
   */
  private createTapEventFromString(tapEventString: string, tapEventHeaders: Array<string>) {
    const tapEventDetails = tapEventString.split(',');

    const tapEvent: InterACT.Interfaces.TfnTaps.TfnTap = <any>{
      scheme: this.userService.convertSchemeToLink(this.userService.selectedScheme)
    };

    (<any>tapEvent).status = 'NOT_UPLOADED';

    for (let c = 0; c < tapEventDetails.length; c++) {
      const dataItem = tapEventDetails[c];
      const propertyName = tapEventHeaders[c];
      // Default tap spec has field names starting with an uppercase, but we use lowercase as standard with json
      const propertyNameStartingLowerCase = propertyName.charAt(0).toLowerCase() + propertyName.slice(1);
      if (dataItem) {
        tapEvent[propertyNameStartingLowerCase] = dataItem;
      }
    }

    return tapEvent;
  }

  /**
   * A method that directs the user to the tap events list state
   */
  private goToTapEventsList = () => {
    this.$state.go('tap-events-list');
  }

  /**
   * A method which is used to instantiate our controller
   */
  private activate() {
    this.breadcrumbService.setBreadcrumbs([{
      name: 'TAP_EVENTS',
      sref: 'tap-events'
    }]);

    this.ActToolbarService.getToolbar('primary').then((toolbar) => {
      toolbar.setTitle('TAP_EVENTS');
    });

    this.PrimaryAction.setActions([{
      name: 'CREATE_JOURNEY_CONSTRUCTION_RULE',
      action: this.goToTapEventsList
    }]);

    this.getTapTranslationRequests(1, this.paginatorData.perPageCount);
  }
}